import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Input, Radio, Select, message } from 'antd';
import { IGetDocumentItem } from 'types/document';
import { TAG_OPTIONS } from '../UploadDocumentModal';
import { saveOrUpdateDocument } from 'api/documentApi';
import { gpSaveOrUpdateDocument } from 'api/group';
import s from './s.module.less';
import { EMeetingUserType } from 'types/zoom';

interface EditDocumentModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialData?: IGetDocumentItem;
    clientId: number;
    isGroupPractice?: boolean;
}

const EditDocumentModal: React.FC<EditDocumentModalProps> = ({ open, onClose, onSuccess, initialData, clientId, isGroupPractice }) => {
    const [form] = Form.useForm();
    const isPatientDocument = useMemo(() => {
        return initialData?.createdByType === EMeetingUserType.KLARITY_USER;
    }, [initialData]);
    const [loading, setLoading] = useState(false);
    const [initialVisibleToPatient, setInitialVisibleToPatient] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (open && initialData) {
            const initialVisible = initialData.visibleToPatient;
            setInitialVisibleToPatient(initialVisible);
            form.setFieldsValue({
                name: initialData.name,
                tag: initialData.tag,
                visibleToPatient: initialVisible,
                note: initialData.note || '',
            });
        } else if (open) {
            setInitialVisibleToPatient(undefined);
            form.resetFields();
        }
    }, [open, initialData, form]);

    const handleOk = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const saveApi = isGroupPractice ? gpSaveOrUpdateDocument : saveOrUpdateDocument;
            const res = await saveApi([{
                name: values.name,
                tag: values.tag,
                note: values.note || '',
                visibleSubjectIds: values.visibleToPatient ? [clientId] : undefined,
                id: initialData?.id,
            }], clientId.toString());
            if (!res.error) {
                message.success('Document edited successfully.');
                onSuccess();
                form.resetFields();
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };


    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleClose}
            footer={[
                <Button onClick={handleClose} className={s.cancelButton}>Cancel</Button>,
                <Button type="primary" onClick={handleOk} className={s.uploadButton} loading={loading}>Save</Button>,
            ]}
            width={640}
            className={s.modal}
            title="Edit document"
        >
            <Form form={form} layout="vertical" className={s.form}>
                <Form.Item
                    label="Name"
                    name="name"
                    rules={[{ required: true, message: 'Please input the document name' }]}
                    className={s.formItem}
                >
                    <Input placeholder="Enter file name" />
                </Form.Item>

                <Form.Item
                    name="tag"
                    label="Tag"
                    rules={[{ required: true, message: 'Please select or enter a tag' }]}
                    className={s.formItem}
                >
                    <Select
                        placeholder="Select or enter tags"
                        className={s.tagSelect}
                        mode="tags"
                        allowClear
                        maxTagCount={3}
                        maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                        tokenSeparators={[',']}
                    >
                        {TAG_OPTIONS.map((tag) => (
                            <Select.Option key={tag} value={tag}>
                                {tag}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {!isPatientDocument && <Form.Item
                    label="Visible to patient?"
                    name="visibleToPatient"
                    className={s.formItem}
                    initialValue={true}
                >
                    <Radio.Group className={s.radioGroup}>
                        <Radio value={true}>Yes</Radio>
                        <Radio value={false}>No</Radio>
                    </Radio.Group>
                </Form.Item>}

                <Form.Item
                    label={isPatientDocument ? 'Note from patient' : 'Note to provider'}
                    className={s.formItem}
                    shouldUpdate={(prevValues, currentValues) => prevValues.visibleToPatient !== currentValues.visibleToPatient}
                >
                    {({ getFieldValue }) => {
                        const currentVisibleToPatient = getFieldValue('visibleToPatient');
                        // 如果初始值是 true，则 note 字段始终 disabled
                        // 如果初始值是 false，则只有当当前值是 true 时，note 字段才 enabled
                        const isNoteDisabled = initialVisibleToPatient === true
                            ? true
                            : (currentVisibleToPatient === false || isPatientDocument);

                        return (
                            <div>
                                {!isPatientDocument && <div className={s.noteDescription}>
                                    Optional message to let the patient know what this document is for
                                </div>}
                                <Form.Item
                                    name="note"
                                    noStyle
                                >
                                    <Input
                                        placeholder="Enter note to patient"
                                        disabled={isNoteDisabled}
                                    />
                                </Form.Item>
                            </div>
                        );
                    }}
                </Form.Item>
            </Form>
        </Modal >
    );
};

export default EditDocumentModal;

